export interface CdTDocument {
  id: string;
  tag: DocumentTag;
  name: string;
  mimeType: string;
  createdAt: string;
  createdBy?: string;
  size: number;
  // Optional fields used by the UI to reflect upload state and errors
  uploadState?: 'provided' | 'not-applicable' | 'waiting' | 'error';
  uploadError?: string;
  blob?: Blob;
  /**
   * Nom, sans extension, sous lequel proposer le fichier au téléchargement quand il diffère du
   * nom affiché : celui d'un contrat sur le disque réseau du pôle social
   * (RG-contrat-nom-de-telechargement). Absent, le téléchargement reprend le nom affiché.
   */
  downloadName?: string;
  /**
   * Orientation retenue de chaque page, en degrés dans le sens des aiguilles d'une montre
   * (RG-document-orientation-memorisee). Clés : numéro de page à partir de "1" ; une image n'a
   * que la page "1". Clairsemé : une page absente est droite. Absent sur un document que
   * l'API ne sert pas, comme le PDF du contrat généré.
   */
  orientations?: PageOrientations;
}

/**
 * Document tel que l'API le renvoie à sa création (`POST /contracts/documents`). Il fait foi sur
 * le fichier : une photo HEIC/HEIF y est déjà convertie en JPEG — nom en `.jpg`, type
 * `image/jpeg`, autre taille. Il n'est encore rattaché à aucune pièce, d'où un `tag` nul.
 */
export type CreatedDocument = Omit<CdTDocument, 'tag'> & { tag: DocumentTag | null };

/** Quart de tour, en degrés dans le sens des aiguilles d'une montre. */
export type Orientation = 0 | 90 | 180 | 270;

/** Orientation de chaque page tournée, indexée par numéro de page à partir de "1". */
export type PageOrientations = Record<string, Orientation>;

export type DocumentTag = (typeof DocumentTags.values)[number];

export class DocumentTags {
  static values = [
    'identityDocument',
    'residentPermit',
    'proofOfResidence',
    'proofOfAccommodation',
    'hostIdentityDocument',
    'hostProofOfResidence',
    'rib',
    'socialSecurityCertificate',
    'contract',
    'signedContract',
    'drivingLicense',
    'securitySheet',
    'signedDrivingLicense',
    'signedSecuritySheet',
    'amendment',
    'signedAmendment',
  ] as const;

  static labels: Record<DocumentTag, string> = {
    identityDocument: "Pièce d'identité",
    residentPermit: 'Titre de séjour',
    proofOfResidence: 'Justificatif de domicile',
    proofOfAccommodation: "Attestation d'hébergement",
    hostIdentityDocument: "Pièce d'identité de l'hébergeant",
    hostProofOfResidence: "Justificatif de domicile de l'hébergeant",
    rib: 'RIB',
    socialSecurityCertificate: 'Attestation de sécurité sociale',
    contract: 'Contrat de travail',
    signedContract: 'Contrat signé',
    drivingLicense: 'Permis de conduire',
    securitySheet: 'Fiche de sécurité',
    signedDrivingLicense: 'Permis de conduire signé',
    signedSecuritySheet: 'Fiche de sécurité signée',
    amendment: 'Avenant',
    signedAmendment: 'Avenant signé',
  };

  static proofOfAccommodationTags: DocumentTag[] = [
    'proofOfAccommodation',
    'hostIdentityDocument',
    'hostProofOfResidence',
  ];
}

export interface DocumentTagAndId {
  tag: DocumentTag;
  id: string;
}

export const acceptedDocumentsMimeTypes = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'application/pdf',
  'image/heic',
  'image/heif',
];
